// Switches for features that are turned on per school (or teacher): the subscription flags (they arrive with the
// profile, $userConfig.effectiveSubscriptionFlags), or localStorage <name>=1 for one browser, for testing.
import { derived, readable } from 'svelte/store';
import { userConfig } from '../components/DataStore';

export function localFlag(name) {
  try {
    return window.localStorage.getItem(name) === '1';
  } catch (e) {
    return false;
  }
}

/** The top bar's search on /api/v2/search: suggestions while typing, and the results page with context. */
export const newSearch = derived(
  userConfig,
  ($config) => $config?.effectiveSubscriptionFlags?.newSearch === true || localFlag('newSearch'),
);

/** The Audio Studio page and its left-menu item (the teacher's or the school's flag). */
export const audioStudio = derived(
  userConfig,
  ($config) => $config?.effectiveSubscriptionFlags?.audioStudio === true || localFlag('audioStudio'),
);

/** Audio Studio's Internal tools: raw SSML, timing JSON import and export, batch generate. */
export const audioStudioInternal = derived(
  userConfig,
  ($config) => $config?.effectiveSubscriptionFlags?.audioStudioInternal === true || localFlag('audioStudioInternal'),
);

/**
 * Avatars drawn by the app (utils/avatar.js) and the Change avatar dialog, for the teacher's own account or the
 * whole school. Off: the Gravatar picture as before.
 */
export const sensayAvatars = derived(
  userConfig,
  ($config) => $config?.effectiveSubscriptionFlags?.sensayAvatars === true || localFlag('sensayAvatars'),
);

/**
 * The new Admin Portal (/admin-portal) for a school's admin, next to the classic Organization Management page, which
 * stays as it is. The school's flag, or localStorage adminPortalV2=1 in one browser.
 */
export const adminPortalV2 = derived(
  userConfig,
  ($config) => $config?.effectiveSubscriptionFlags?.adminPortalV2 === true || localFlag('adminPortalV2'),
);

/**
 * Practice Packs in the app (pilot): the user's own `practicePacks` flag ($userConfig.serverFlags, from
 * currentSubscriptionFlags), as the backend checks it; the school's flags do not turn it on. Staff with
 * `practicePackManagement` use the catalog too. Everyone else keeps the link to the Practice Packs website.
 */
export const practicePacksEnabled = derived(
  userConfig,
  ($config) => $config?.serverFlags?.practicePacks === true || $config?.serverFlags?.practicePackManagement === true,
);

/** Sensay staff who build, publish and retire Practice Packs (the user's own `practicePackManagement` flag). */
export const practicePackManager = derived(userConfig, ($config) => $config?.serverFlags?.practicePackManagement === true);

// the flags arrive with the profile; a page that differs by flag waits for them, at most 3 seconds
const waited = readable(false, (set) => {
  const timer = setTimeout(() => set(true), 3000);
  return () => clearTimeout(timer);
});

/** Whether the school's flags are known (or 3 seconds passed without them): until then show neither version. */
export const flagsKnown = derived(
  [userConfig, waited],
  ([$config, $waited]) => $config?.effectiveSubscriptionFlags !== undefined || $waited,
);
